/**
 * FF-QA-002 / FF-QA-141: whether the boot `POST /auth/refresh` can be skipped.
 *
 * The refresh cookie is httpOnly, so the client cannot see whether one exists.
 * This marker is the client's own record of how its last session ended, in
 * three states:
 *
 *   '1'     a sign-in or refresh succeeded — refresh at boot.
 *   '0'     the user logged out, or the server rejected the refresh token —
 *           nothing can be refreshed, so boot straight to anonymous with no
 *           request (the 401 the finding was about).
 *   absent  UNKNOWN — refresh at boot, exactly as before the marker existed.
 *
 * Absent must never mean "skip". Two populations have a live session and no
 * marker: every browser signed in on the release before the marker shipped
 * (skipping would sign all of them out on upgrade), and a device whose first
 * boot after that upgrade happens offline — the dead-zone fallback to the
 * persisted offline identity lives inside the refresh's network-failure path,
 * so skipping the call would strand that device on a sign-in screen it cannot
 * use (D24 §9). Storage that throws is also UNKNOWN, for the same reason.
 *
 * The marker holds no secret; it only decides whether to make a request.
 */

const SESSION_MARKER_KEY = 'ff.hadSession'

/** Record how the session stands: `true` after a successful sign-in/refresh, `false` after logout or a rejected refresh. */
export function writeSessionMarker(live: boolean): void {
  try {
    localStorage.setItem(SESSION_MARKER_KEY, live ? '1' : '0')
  } catch {
    /* storage unavailable: the marker stays unknown and the boot refresh runs */
  }
}

/** True only when this browser KNOWS its session ended ('0'). Absent, '1', or unreadable storage all mean "refresh". */
export function sessionKnownEnded(): boolean {
  try {
    return localStorage.getItem(SESSION_MARKER_KEY) === '0'
  } catch {
    return false
  }
}
